import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Share2, Download, Heart, Star, Flame, Trophy, Clock, Smile } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_EMOJIS = { 1: '😢', 2: '😟', 3: '😐', 4: '😊', 5: '🤩' };
const MOOD_COLORS = { 1: '#FF6B6B', 2: '#FFC8DD', 3: '#FDF0D5', 4: '#B9FBC0', 5: '#A2D2FF' };
const STAGE_LABELS = ['Egg', 'Baby', 'Teen', 'Adult', 'Legendary'];

export default function ShareCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/share-card`, { withCredentials: true });
      setData(data);
    } catch (e) {
      console.error('Failed to fetch share card:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateCard = useCallback(() => {
    if (!data || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 400;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 600, 400);
    grad.addColorStop(0, '#FFFAF0');
    grad.addColorStop(0.5, '#FFF5E6');
    grad.addColorStop(1, '#FFF0F5');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 600, 400);

    // Decorative dots
    ctx.fillStyle = '#D0D0D0';
    for (let x = 15; x < 600; x += 20) {
      for (let y = 15; y < 400; y += 20) {
        ctx.beginPath();
        ctx.arc(x, y, 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Card border
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 4;
    ctx.roundRect(10, 10, 580, 380, 24);
    ctx.stroke();

    // Header
    ctx.fillStyle = '#1C1C1E';
    ctx.font = 'bold 28px Fredoka, sans-serif';
    ctx.fillText('Focus Critter', 30, 50);
    ctx.font = '14px Nunito, sans-serif';
    ctx.fillStyle = '#888';
    ctx.fillText('Friend for Work & Study', 30, 70);

    // Pet name & stage
    ctx.fillStyle = '#1C1C1E';
    ctx.font = 'bold 22px Fredoka, sans-serif';
    ctx.fillText(`${data.pet_name}`, 30, 110);
    ctx.font = '14px Nunito, sans-serif';
    ctx.fillStyle = '#CDB4DB';
    ctx.fillText(`${data.stage_title} • Level ${data.pet_level}`, 30, 130);

    // Stats boxes
    const stats = [
      { label: 'Streak', value: `${data.streak}d`, color: '#FFC8DD', icon: '🔥' },
      { label: 'Points', value: `${data.total_points}`, color: '#FDF0D5', icon: '⭐' },
      { label: 'Tasks', value: `${data.tasks_completed}`, color: '#B9FBC0', icon: '✓' },
      { label: 'Focus', value: `${data.total_focus_minutes}m`, color: '#A2D2FF', icon: '⏰' },
    ];

    stats.forEach((s, i) => {
      const x = 30 + i * 138;
      const y = 150;
      ctx.fillStyle = s.color;
      ctx.beginPath();
      ctx.roundRect(x, y, 125, 70, 12);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#1C1C1E';
      ctx.font = 'bold 24px Fredoka, sans-serif';
      ctx.fillText(`${s.icon} ${s.value}`, x + 12, y + 35);
      ctx.font = '12px Nunito, sans-serif';
      ctx.fillStyle = '#555';
      ctx.fillText(s.label, x + 12, y + 55);
    });

    // Multiplier badge
    if (data.multiplier > 1) {
      ctx.fillStyle = '#CDB4DB';
      ctx.beginPath();
      ctx.roundRect(430, 95, 130, 35, 16);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#1C1C1E';
      ctx.font = 'bold 16px Fredoka, sans-serif';
      ctx.fillText(`⚡ ${data.multiplier}x points`, 442, 118);
    }

    // Mood trend
    ctx.fillStyle = '#1C1C1E';
    ctx.font = 'bold 14px Fredoka, sans-serif';
    ctx.fillText('This Week\'s Mood:', 30, 255);
    data.week_moods.forEach((m, i) => {
      const x = 30 + i * 40;
      if (m.mood) {
        ctx.fillStyle = MOOD_COLORS[m.mood];
        ctx.beginPath();
        ctx.roundRect(x, 265, 30, 30, 8);
        ctx.fill();
        ctx.font = '16px sans-serif';
        ctx.fillText(MOOD_EMOJIS[m.mood], x + 5, 287);
      } else {
        ctx.fillStyle = '#eee';
        ctx.beginPath();
        ctx.roundRect(x, 265, 30, 30, 8);
        ctx.fill();
        ctx.fillStyle = '#ccc';
        ctx.font = '14px sans-serif';
        ctx.fillText('—', x + 9, 285);
      }
    });

    // Achievements count
    ctx.fillStyle = '#1C1C1E';
    ctx.font = 'bold 14px Fredoka, sans-serif';
    ctx.fillText(`🏆 ${data.achievements_count} Achievements`, 320, 287);

    // Footer
    ctx.fillStyle = '#FFC8DD';
    ctx.fillRect(10, 330, 580, 50);
    ctx.fillStyle = '#1C1C1E';
    ctx.font = 'bold 18px Fredoka, sans-serif';
    ctx.fillText('❤️ Join me on Focus Critter!', 30, 360);
    ctx.font = '12px Nunito, sans-serif';
    ctx.fillStyle = '#666';
    ctx.fillText('Your cute ADHD focus companion', 350, 360);
  }, [data]);

  useEffect(() => { if (data) generateCard(); }, [data, generateCard]);

  const downloadCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `focus-critter-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Card downloaded!');
  };

  const shareCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.toBlob(async (blob) => {
        if (navigator.share) {
          const file = new File([blob], 'focus-critter.png', { type: 'image/png' });
          await navigator.share({ title: 'My Focus Critter Stats', files: [file] });
        } else {
          downloadCard();
        }
      });
    } catch (e) {
      downloadCard();
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Generating card...</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="share-card">
      <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
        <Share2 className="w-5 h-5" /> Share Your Progress
      </h3>

      {/* Canvas card */}
      <div className="neo-card p-4 flex justify-center">
        <canvas ref={canvasRef} className="max-w-full rounded-xl border border-gray-200" style={{ maxHeight: 400 }} data-testid="share-canvas" />
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-center">
        <button onClick={downloadCard} className="neo-btn px-6 py-3 bg-[#A2D2FF] flex items-center gap-2" data-testid="download-card-btn">
          <Download className="w-5 h-5" />
          <span>Download</span>
        </button>
        <button onClick={shareCard} className="neo-btn px-6 py-3 bg-[#B9FBC0] flex items-center gap-2" data-testid="share-card-btn">
          <Share2 className="w-5 h-5" />
          <span>Share</span>
        </button>
      </div>

      {/* Quick stats preview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="neo-card p-3 text-center bg-[#FFC8DD]">
          <Flame className="w-5 h-5 mx-auto mb-1" />
          <p className="font-bold font-['Fredoka']">{data.streak}d streak</p>
        </div>
        <div className="neo-card p-3 text-center bg-[#FDF0D5]">
          <Star className="w-5 h-5 mx-auto mb-1" fill="currentColor" />
          <p className="font-bold font-['Fredoka']">{data.total_points} pts</p>
        </div>
        <div className="neo-card p-3 text-center bg-[#B9FBC0]">
          <Trophy className="w-5 h-5 mx-auto mb-1" />
          <p className="font-bold font-['Fredoka']">{data.tasks_completed} tasks</p>
        </div>
        <div className="neo-card p-3 text-center bg-[#A2D2FF]">
          <Clock className="w-5 h-5 mx-auto mb-1" />
          <p className="font-bold font-['Fredoka']">{data.total_focus_minutes}m focus</p>
        </div>
      </div>
    </div>
  );
}
